/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Highlighter from 'react-highlight-words';
import PropTypes from 'prop-types';

// Component imports
import { Button, Dropdown, Grid, Menu, Space, Table, Tooltip, Typography } from 'antd';
import { CopyOutlined, EllipsisOutlined, EyeOutlined, FolderViewOutlined, HighlightOutlined } from '@ant-design/icons';
import DeploymentChannel from '../UI/DeploymentChannel';
import { EditIcon } from '../Icons/EditIcon';
import { CodeIcon } from '../Icons/CodeIcon';

// Misc imports
import { BOT_DEPLOYMENTS_CHANNELS, DEFAULT_PAGINATION_SIZE, LANGUAGE_OPTIONS } from '../../constants/main';
import { flatten } from '../../utils/object/flatten';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

const { useBreakpoint } = Grid;
const BotDeploymentsTable = ({
  botDeployments=[],
  filteredData=[],
  hasEditAccess=false,
  loader,
  handlePreviewDeployment=() => {},
  handleCloneDeployment=() => {},
  handleEditDeployment=() => {},
  handleSnippetIconModal=() => {},
  handleTableChange=() => {}
}) => {
  const screens = useBreakpoint();
  const isMobileSize = screens.xs;
  const { t } = useTranslation();
  const { botLanguages } = useBotLanguage();
  const { handleMouseDown, isDragging } = useDragDetection();

  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const onPreviewClick = (deployment) => {
    handlePreviewDeployment(deployment);
  };

  const onCloneClick = (record) => {
    handleCloneDeployment(record);
  };

  const onEditClick = (record) => {
    handleEditDeployment(record);
  };

  const onSnippetIconClick = (record) => {
    handleSnippetIconModal(record);
  };

  const columns = [
    {
      title: t('Deployment Name'),
      dataIndex: 'displayName',
      key: 'displayName',
      fixed: 'left',
      align: 'left',
      width: '40%',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
      render: (text, record) => {
        return record ? (
          <Space>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[filteredData.search]}
              autoEscape
              textToHighlight={record.displayName}
            />

            {
              // If there are other search results within the nested values, show an indicator
              !record.displayName.includes(filteredData.search) ? (
                <Tooltip overlayClassName="pointer-events-none" title={t('More results within detailed view')}>
                  <HighlightOutlined className="text-primary" />
                </Tooltip>
              ) : null
            }
          </Space>
        ) : null;
      }
    },
    {
      title: t('Channel'),
      dataIndex: 'platform',
      filters: Object.entries(BOT_DEPLOYMENTS_CHANNELS).map(([key, value]) => ({
        text: <DeploymentChannel deploymentChannelTitle={value.name} deploymentChannelIcon={value.icon} />,
        value: key
      })),
      onFilter: (value, record) => record.platform === value,
      filterIcon: useTableFilterIcon,
      key: 'platform',
      responsive: ['sm'],
      width: '25%',
      render: (_, record) => {
        return (
          <DeploymentChannel deploymentChannelTitle={record.platformName} deploymentChannelIcon={record.platformIcon} />
        );
      }
    },
    {
      title: t('Language'),
      dataIndex: 'language',
      key: 'language',
      filters: [
        {
          text: t('No selected language'),
          value: 'no_selected_language'
        },
        ...((botLanguages && botLanguages.length > 0) ? botLanguages.map(language => ({
          text: LANGUAGE_OPTIONS.find(lang => lang?.key === language)?.label,
          value: language
        })) : [])
      ],
      onFilter: (value, record) => {
        if (value === 'no_selected_language') {
          return !record?.settings?.language;
        }
        return record?.settings?.language?.toLowerCase() === value?.toLowerCase();
      },
      filterIcon: useTableFilterIcon,
      render: (_text, record) => {
        const language = LANGUAGE_OPTIONS.find(language => language?.key === record?.settings?.language);
        return language ? language?.label : (record?.settings?.language ? record?.settings?.language : '-');
      }
    },
    {
      title: t('Javascript Snippet'),
      responsive: ['md'],
      dataIndex: 'snippet',
      key: 'snippet',
      width: '15%',
      align: 'center',
      render: (_, record) => {
        if (record.platform === BOT_DEPLOYMENTS_CHANNELS.MICROSOFT_WEB_CHAT.key) {
          return (
            <Space onClick={(e) => e.stopPropagation()}>
              <Tooltip overlayClassName="pointer-events-none" title={t('View the deployment snippet')}>
                <Button
                  type="text"
                  className="bg-layout border-raduis-base bordered"
                  icon={<CodeIcon />}
                  loading={loader}
                  onClick={() => onSnippetIconClick(record)}
                />
              </Tooltip>
            </Space>
          );
        }else {
          return '-';
        }
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: 160,
      align: 'center',
      render: record => {
        if (record.scope === 'USER') {
          if (hasEditAccess) {
            return (
              <div style={{ width: 160 }} className="text-left m-auto">
                <Space onClick={(e) => e.stopPropagation()}>
                  {
                    BOT_DEPLOYMENTS_CHANNELS[record.platform]?.isPreviewAvailable ? (
                      <Tooltip overlayClassName="pointer-events-none" title={t('View Deployment')}>
                        <Button
                          hidden={isMobileSize}
                          shape="circle"
                          type="text"
                          icon={<EyeOutlined />}
                          loading={loader}
                          onClick={() => onPreviewClick(record)}
                        />
                      </Tooltip>
                    ) : null
                  }

                  <Tooltip overlayClassName="pointer-events-none" title={t('Clone Deployment')}>
                    <Button
                      hidden={isMobileSize}
                      shape="circle"
                      type="text"
                      icon={<CopyOutlined />}
                      loading={loader}
                      onClick={() => onCloneClick(record)}
                    />
                  </Tooltip>

                  <Tooltip overlayClassName="pointer-events-none" title={t('Edit Deployment')}>
                    <Button
                      shape="circle"
                      type="text"
                      icon={<EditIcon />}
                      loading={loader}
                      hidden={record.isPreview || isMobileSize}
                      onClick={() => onEditClick(record)}
                    />
                  </Tooltip>

                  {
                    isMobileSize ? (
                      <Dropdown
                        trigger="click"
                        placement="bottomRight"
                        overlay={
                          <Menu
                            onClick={(item) => {
                              switch (item.key) {
                                case 'preview':
                                  onPreviewClick(record);
                                  break;
                                case 'clone':
                                  onCloneClick(record);
                                  break;
                                case 'edit':
                                  onEditClick(record);
                                  break;
                                default:
                                  break;
                              }
                            }}
                            items={[
                              {
                                key: 'preview',
                                icon: <EyeOutlined hidden={!BOT_DEPLOYMENTS_CHANNELS[record.platform]?.isPreviewAvailable} className="fs-xl mb-2 mt-2 ml-2"/>,
                                label: ''
                              },
                              {
                                key: 'clone',
                                icon: <CopyOutlined className="fs-xl mb-2 mt-2 ml-2"/>,
                                label: ''
                              },
                              {
                                key: 'edit',
                                icon: <EditIcon className="fs-xl mb-2 mt-2 ml-2"/>,
                                label: ''
                              }
                            ]}
                          />
                        }
                      >
                        <Button
                          shape="circle"
                          type="text"
                          icon={<EllipsisOutlined className="fw-bold fs-xxl" rotate={90}/>}
                          loading={loader}
                        />
                      </Dropdown>
                    ) : null
                  }
                </Space>
              </div>
            );
          } else if (!hasEditAccess) {
            return (
              <Space>
                <Tooltip overlayClassName="pointer-events-none" title={t('View Deployment')}>
                  <Button
                    hidden={isMobileSize}
                    shape="circle"
                    type="text"
                    icon={<EyeOutlined />}
                    loading={loader}
                    onClick={() => onPreviewClick(record)}
                  />
                </Tooltip>

                <Tooltip overlayClassName="pointer-events-none" title={t('View deployment settings')}>
                  <Button
                    shape="circle"
                    type="text"
                    icon={<FolderViewOutlined/>}
                    loading={loader}
                    onClick={() => onEditClick(record)}
                  />
                </Tooltip>
              </Space>
            );
          }
        } else {
          return (
            <Typography.Text>
              {t('No actions available')}
            </Typography.Text>
          );
        }
      }
    }
  ].filter(column => !column.hidden);

  const data = botDeployments
    .map((deployment) => {
      return {
        ...deployment,
        platformIcon: Object.values(BOT_DEPLOYMENTS_CHANNELS).find((channel) => channel.key === deployment.platform)?.icon,
        platformName: Object.values(BOT_DEPLOYMENTS_CHANNELS).find((channel) => channel.key === deployment.platform)?.name
      };
    })
    .filter((deployment) => !deployment.isPreview).sort((a, b) => {
      return a.displayName < b.displayName ? -1 : 1;
    });

  return (
    <Table
      size="small"
      columns={columns}
      onRow={(record) => ({
        onMouseDown: handleMouseDown,
        onClick: () => {
          if(!isDragging() && hasEditAccess) {
            onEditClick(record);
          }
        }
      })}
      rowClassName="cursor-pointer"
      pagination={{
        size: 'default',
        pageSize: pageSize,
        showSizeChanger: true,
        showTotal: (total) => `${t('Total Deployments')}: ${total}`
      }}
      loading={loader}
      onChange={(pagination, filters, sorter) => {
        setPageSize(pagination.pageSize);
        handleTableChange(pagination, filters, sorter);
      }}
      rowKey="id"
      dataSource={
        data && filteredData ? (data || [])?.filter(
          deployment => {
            const stringifiedDeployment = JSON.stringify(Object.values(flatten(deployment))).toLowerCase();
            return stringifiedDeployment.includes((filteredData.search || '').toLowerCase());
          }
        ) : (data || [])
      }
    />
  );
};

BotDeploymentsTable.propTypes = {
  botDeployments: PropTypes.array,
  loader: PropTypes.bool,
  hasEditAccess: PropTypes.bool,
  handleCloneDeployment: PropTypes.func,
  handlePreviewDeployment: PropTypes.func,
  handleSnippetIconModal: PropTypes.func,
  handleEditDeployment: PropTypes.func,
  handleTableChange: PropTypes.func,
  filteredData: PropTypes.object
};
export default BotDeploymentsTable;
